import { useEffect, useMemo, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useToast } from '../components/ToastProvider.jsx'
import * as userApi from '../services/userApi'
import { ROLE_OPTIONS, isStaff, roleLabel } from '../utils/permissions'

const EMPTY_CREATE = {
  username: '',
  name: '',
  department: '',
  position: '',
  password: '',
  role: 'staff',
  canTransact: false,
}

function transactLabel(user) {
  if (!isStaff(user)) return '항상 가능'
  return user?.permissions?.canTransact ? '허용' : '없음'
}

function activeLabel(user) {
  return user?.active === false ? '중지' : '활성'
}

function CreateUserModal({ open, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_CREATE)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const staffSelected = form.role === 'staff'

  useEffect(() => {
    if (!open) return
    setForm(EMPTY_CREATE)
    setError('')
  }, [open])

  if (!open) return null

  async function handleSubmit(event) {
    event.preventDefault()
    if (!form.username.trim() || !form.name.trim() || !form.password) {
      setError('아이디, 이름, 비밀번호를 입력해 주세요.')
      return
    }
    if (form.password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSubmit({
        username: form.username.trim(),
        name: form.name.trim(),
        department: form.department.trim(),
        position: form.position.trim(),
        password: form.password,
        role: form.role,
        permissions: { canTransact: staffSelected ? Boolean(form.canTransact) : false },
      })
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form className="modal-panel modal-panel--form" onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
        <h2 className="modal-title">새 사용자</h2>
        <label className="field">
          <span>아이디</span>
          <input className="input" value={form.username} autoComplete="off" onChange={(e) => setForm((c) => ({ ...c, username: e.target.value }))} />
        </label>
        <label className="field">
          <span>이름</span>
          <input className="input" value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} />
        </label>
        <label className="field">
          <span>부서</span>
          <input className="input" value={form.department} onChange={(e) => setForm((c) => ({ ...c, department: e.target.value }))} />
        </label>
        <label className="field">
          <span>직책</span>
          <input className="input" value={form.position} onChange={(e) => setForm((c) => ({ ...c, position: e.target.value }))} />
        </label>
        <label className="field">
          <span>비밀번호</span>
          <input className="input" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm((c) => ({ ...c, password: e.target.value }))} />
        </label>
        <label className="field">
          <span>역할</span>
          <select className="input" value={form.role} onChange={(e) => setForm((c) => ({ ...c, role: e.target.value, canTransact: e.target.value === 'staff' ? c.canTransact : false }))}>
            {ROLE_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className={`check ${staffSelected ? '' : 'is-disabled'}`}>
          <input
            type="checkbox"
            checked={staffSelected ? form.canTransact : false}
            disabled={!staffSelected}
            onChange={(e) => setForm((c) => ({ ...c, canTransact: e.target.checked }))}
          />
          입출고 권한
        </label>
        {staffSelected ? null : <p className="muted">관리자/매니저는 입출고 권한이 항상 허용됩니다.</p>}
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
            {saving ? '저장 중...' : '생성'}
          </button>
        </div>
      </form>
    </div>
  )
}

function EditUserModal({ open, user, currentUserId, onClose, onSubmit }) {
  const [form, setForm] = useState({ name: '', department: '', position: '', role: 'staff', active: true, canTransact: false })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const isSelf = user && String(user.id) === String(currentUserId)
  const staffSelected = form.role === 'staff'

  useEffect(() => {
    if (!open || !user) return
    setForm({
      name: user.name || '',
      department: user.department || '',
      position: user.position || '',
      role: user.role || 'staff',
      active: user.active !== false,
      canTransact: Boolean(user.permissions?.canTransact),
    })
    setError('')
  }, [open, user])

  if (!open || !user) return null

  async function handleSubmit(event) {
    event.preventDefault()
    if (!form.name.trim()) {
      setError('이름을 입력해 주세요.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name.trim(),
        department: form.department.trim(),
        position: form.position.trim(),
        permissions: { canTransact: staffSelected ? Boolean(form.canTransact) : false },
      }
      if (!isSelf) {
        payload.role = form.role
        payload.active = form.active
      }
      await onSubmit(user.id, payload)
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form className="modal-panel modal-panel--form" onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
        <h2 className="modal-title">사용자 수정</h2>
        <p className="modal-model">{user.username}</p>
        <label className="field">
          <span>이름</span>
          <input className="input" value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} />
        </label>
        <label className="field">
          <span>부서</span>
          <input className="input" value={form.department} onChange={(e) => setForm((c) => ({ ...c, department: e.target.value }))} />
        </label>
        <label className="field">
          <span>직책</span>
          <input className="input" value={form.position} onChange={(e) => setForm((c) => ({ ...c, position: e.target.value }))} />
        </label>
        <label className="field">
          <span>역할</span>
          <select
            className="input"
            value={form.role}
            disabled={isSelf}
            onChange={(e) => setForm((c) => ({ ...c, role: e.target.value, canTransact: e.target.value === 'staff' ? c.canTransact : false }))}
          >
            {ROLE_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className={`check ${isSelf ? 'is-disabled' : ''}`}>
          <input
            type="checkbox"
            checked={form.active}
            disabled={isSelf}
            onChange={(e) => setForm((c) => ({ ...c, active: e.target.checked }))}
          />
          계정 활성
        </label>
        {isSelf ? <p className="muted">자신의 역할 변경과 비활성화는 할 수 없습니다.</p> : null}
        <label className={`check ${staffSelected ? '' : 'is-disabled'}`}>
          <input
            type="checkbox"
            checked={staffSelected ? form.canTransact : false}
            disabled={!staffSelected}
            onChange={(e) => setForm((c) => ({ ...c, canTransact: e.target.checked }))}
          />
          입출고 권한
        </label>
        {staffSelected ? null : <p className="muted">관리자/매니저는 입출고 권한이 항상 허용됩니다.</p>}
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </form>
    </div>
  )
}

function PasswordModal({ open, user, onClose, onSubmit }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setPassword('')
    setConfirm('')
    setError('')
  }, [open])

  if (!open || !user) return null

  async function handleSubmit(event) {
    event.preventDefault()
    if (!password || password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.')
      return
    }
    if (password !== confirm) {
      setError('비밀번호 확인이 일치하지 않습니다.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSubmit(user.id, password)
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form className="modal-panel modal-panel--form" onClick={(event) => event.stopPropagation()} onSubmit={handleSubmit}>
        <h2 className="modal-title">비밀번호 재설정</h2>
        <p className="modal-model">{user.username}</p>
        <label className="field">
          <span>새 비밀번호</span>
          <input className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label className="field">
          <span>새 비밀번호 확인</span>
          <input className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>
            {saving ? '변경 중...' : '변경'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default function UsersPage() {
  const { user: currentUser, refreshUser } = useAuth()
  const { show } = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [passwordUser, setPasswordUser] = useState(null)

  const sorted = useMemo(
    () => [...users].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko')),
    [users],
  )

  async function load() {
    setLoading(true)
    setError('')
    try {
      setUsers(await userApi.fetchUsers())
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function handleCreated(payload) {
    await userApi.createUser(payload)
    show('사용자를 생성했습니다.')
    setCreateOpen(false)
    await load()
  }

  async function handleUpdated(id, payload) {
    await userApi.updateUser(id, payload)
    show('사용자 정보를 수정했습니다.')
    setEditing(null)
    await load()
    if (String(id) === String(currentUser?.id) && refreshUser) {
      await refreshUser()
    }
  }

  async function handlePassword(id, password) {
    await userApi.updateUserPassword(id, password)
    show('비밀번호를 변경했습니다.')
    setPasswordUser(null)
  }

  return (
    <div className="page">
      <PageHeader
        title="사용자 관리"
        description="계정, 역할, 입출고 권한을 관리합니다. 사용 중지 계정은 비활성화합니다."
        actions={
          <button type="button" className="btn btn--primary btn--touch" onClick={() => setCreateOpen(true)}>
            새 사용자
          </button>
        }
      />

      {error ? <p className="form-error">{error}</p> : null}
      <p className="result-count">{loading ? '불러오는 중...' : `${sorted.length}명`}</p>

      {sorted.length === 0 && !loading ? (
        <p className="empty">등록된 사용자가 없습니다.</p>
      ) : (
        <>
          <div className="card-list">
            {sorted.map((item) => (
              <article key={item.id} className="part-card">
                <div className="part-card__top">
                  <span className="muted">{item.username}</span>
                  <span className="badge-row">
                    <span className="badge">{roleLabel(item.role)}</span>
                    <span className="badge">{activeLabel(item)}</span>
                  </span>
                </div>
                <strong>{item.name}</strong>
                <span>
                  {item.department || '-'} · {item.position || '-'}
                </span>
                <span className="muted">입출고 {transactLabel(item)}</span>
                <div className="part-card__actions">
                  <button type="button" className="btn btn--ghost btn--touch" onClick={() => setEditing(item)}>
                    수정
                  </button>
                  <button type="button" className="btn btn--ghost btn--touch" onClick={() => setPasswordUser(item)}>
                    비밀번호
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>이름</th>
                  <th>아이디</th>
                  <th>부서</th>
                  <th>직책</th>
                  <th>역할</th>
                  <th>상태</th>
                  <th>입출고 권한</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.name}
                      {String(item.id) === String(currentUser?.id) ? <span className="muted"> (나)</span> : null}
                    </td>
                    <td>{item.username}</td>
                    <td>{item.department || '-'}</td>
                    <td>{item.position || '-'}</td>
                    <td>{roleLabel(item.role)}</td>
                    <td>{activeLabel(item)}</td>
                    <td>{transactLabel(item)}</td>
                    <td>
                      <div className="user-actions">
                        <button type="button" className="btn btn--ghost btn--small" onClick={() => setEditing(item)}>
                          수정
                        </button>
                        <button type="button" className="btn btn--ghost btn--small" onClick={() => setPasswordUser(item)}>
                          비밀번호
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={handleCreated} />
      <EditUserModal
        open={Boolean(editing)}
        user={editing}
        currentUserId={currentUser?.id}
        onClose={() => setEditing(null)}
        onSubmit={handleUpdated}
      />
      <PasswordModal open={Boolean(passwordUser)} user={passwordUser} onClose={() => setPasswordUser(null)} onSubmit={handlePassword} />
    </div>
  )
}
